import {onUserActivity} from "@analytics/activity-utils";
import {
    extendSession,
    getPageSession,
    getSession,
    getTabSession,
    setPageSession,
    setSession,
    setTabSession,
} from "@analytics/session-utils";
import {PinpointSessionEventState} from "./eventMetadata";

export interface AnalyticsIdentity {
    anonId: string;
    userId: string | null;
}

export interface PinpointLifecycleEventState {
    session: PinpointSessionEventState;
    pageSession: string;
}

export interface PinpointSessionLifecycleOptions {
    getIdentity: () => AnalyticsIdentity;
    onEvent: (eventType: string, state: PinpointLifecycleEventState) => void;
}

export function createPinpointSessionLifecycle({getIdentity, onEvent}: PinpointSessionLifecycleOptions) {
    let hasPageFiredOnce = false;

    getPageSession();
    getTabSession();
    const initialSession = getSession();
    const identity = getIdentity();

    const hasChangedAnonymousId = initialSession.anonId && initialSession.anonId !== identity.anonId;

    if (hasChangedAnonymousId) {
        setPageSession();
        setTabSession();
        setSession(30, identity);
    }

    function getEventState(): PinpointLifecycleEventState {
        const session = getSession();
        const pageSession = getPageSession();

        return {
            session: {id: session.id, startMs: session.created, elapsed: session.elapsed},
            pageSession: pageSession.id,
        };
    }

    function emit(eventType: string): void {
        onEvent(eventType, getEventState());
    }

    if (initialSession.isNew || hasChangedAnonymousId) {
        emit("_session.start");
    }

    onUserActivity({
        timeout: 1_800_000,
        throttle: 20_000,
        onIdle: () => emit("_session.stop"),
        onWakeUp: () => {
            setSession(30);
            emit("_session.start");
        },
        onHeartbeat: () => extendSession(30, getIdentity()),
    });

    return {
        getEventState,
        page(): void {
            if (hasPageFiredOnce) {
                setPageSession();
            }

            emit("pageView");
            hasPageFiredOnce = true;
        },
    };
}
